import React from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import { UnstyledButton } from '@/components/ui/Buttons';
import { plato_api_sync_grade_submission_path } from '@/modules/routes';
import cn from '@/utils/cn';
import useGradesTableStore from '../../store/useGradesTableStore';
import { pendingSyncKey } from '../../store/createGradesTableStore';
import { ILatestGradeSyncEvent } from '../../types';
import { SYNC_STATUS_ICON_MAP, SyncStatusIcon } from './SyncStatusIcon';

const PENDING_TIMEOUT_MS = 10 * 60 * 1000;

interface SyncGradeStatusButtonProps extends React.HTMLProps<HTMLButtonElement> {
  assignmentId: number;
  scoreUpdatedAt: string | null;
  studentId: number;
  submissionId: number;
}

interface DeriveStatusArgs {
  event: ILatestGradeSyncEvent | undefined;
  pendingClickedAt: number | undefined;
  scoreUpdatedAt: string | null | undefined;
}

const msOrZero = (timestamp: string | null | undefined) => (
  timestamp ? new Date(timestamp).getTime() : 0
);

// AIDEV-NOTE: lms_sync_events is the source of truth for sync status — see PLAT-17068.
// `in_progress` = the user clicked recently and no newer event has arrived; after
// PENDING_TIMEOUT_MS we drop back so a stuck worker doesn't leave a forever-spinner.
//
// AIDEV-NOTE: PLAT-18052 — the other three states are asked about the *current score value*, not the
// latest attempt. score_updated_at marks when the current value came into being (Assignment#submit!
// on every student submission, set_score_updated_at! on a teacher override), so an attempt that
// predates it describes a value that no longer exists.
//
//   success — a score reached the LMS since the value changed. Existence, not recency: a failed retry
//             after a good sync must not turn a score red that is fine in the LMS, so success is
//             checked first.
//   failed  — a real failure since the value changed. Rate-limited attempts are excluded server-side
//             from last_failure_at, since they reschedule themselves and are still pending.
//   pending — everything else: never attempted for this value (PLAT-18137), a score edited after a
//             successful sync, or a retry still waiting out a rate limit.
export const deriveSyncStatus = ({
  event, pendingClickedAt, scoreUpdatedAt,
}: DeriveStatusArgs): keyof typeof SYNC_STATUS_ICON_MAP => {
  // last_attempt_at counts every attempt including rate-limited ones, which is what lets the spinner
  // stop: it only clears when something newer than the click shows up.
  const lastAttemptAt = msOrZero(event?.last_attempt_at);
  const isPending = pendingClickedAt !== undefined
    && Date.now() - pendingClickedAt < PENDING_TIMEOUT_MS
    && lastAttemptAt <= pendingClickedAt;

  if (isPending) return 'in_progress';

  // A missing score_updated_at means we cannot date the value, so judge on the timestamps themselves.
  // Treating it as epoch zero would let any event count as current, but StudentGradeCell does not
  // render the button at all without one — the two gates have to move together.
  const valueChangedAt = msOrZero(scoreUpdatedAt);

  if (event?.last_success_at && msOrZero(event.last_success_at) >= valueChangedAt) return 'success';
  if (event?.last_failure_at && msOrZero(event.last_failure_at) >= valueChangedAt) return 'failed';

  return 'pending';
};

const ariaLabelForStatus = (status: keyof typeof SYNC_STATUS_ICON_MAP) => {
  if (status === 'failed') return I18n.t('sync_grades_failed');
  if (status === 'in_progress') return I18n.t('syncing_grades_to_lms');
  if (status === 'pending') return I18n.t('pending_sync');

  return I18n.t('grades_synced_to_lms');
};

const SyncGradeStatusButton = ({
  assignmentId,
  className,
  scoreUpdatedAt,
  studentId,
  submissionId,
  onClick,
}: SyncGradeStatusButtonProps) => {
  const event = useGradesTableStore(state => state.gradeSyncEvents[assignmentId]?.[studentId]);
  const pendingClickedAt = useGradesTableStore(state => state.pendingSyncs[pendingSyncKey(assignmentId, studentId)]);
  const markSyncPending = useGradesTableStore(state => state.markSyncPending);

  const status = deriveSyncStatus({ event, pendingClickedAt, scoreUpdatedAt });

  const { mutateAsync } = useMutation({
    mutationFn: () => axios.post(plato_api_sync_grade_submission_path(submissionId)),
    mutationKey: ['SyncGradeMutation', submissionId],
    onSuccess: () => markSyncPending(assignmentId, studentId),
  });

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (status === 'in_progress') return;

    await mutateAsync();

    if (onClick) onClick(e);
  };

  return (
    <UnstyledButton
      aria-label={ariaLabelForStatus(status)}
      className={cn('tw-inline-flex tw-items-center tw-justify-center', className)}
      type="button"
      onClick={handleClick}
    >
      <SyncStatusIcon status={status} />
    </UnstyledButton>
  );
};

export default SyncGradeStatusButton;
